import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  CreditCard, KeySquare, Layers, Ticket, Plus, Trash2, Loader2, Save,
  ShoppingCart, X, Copy, CheckCircle2, ReceiptText, Percent, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, apiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-[#1E2433] bg-[#0b0d13] px-3 py-2 text-sm text-slate-200 outline-none transition-colors focus:border-emerald-500/60";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
      {children}
    </div>
  );
}

const SUBTABS = [
  { id: "keys", label: "Cashfree Keys", icon: KeySquare },
  { id: "plans", label: "Plans", icon: Layers },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "orders", label: "Orders", icon: ReceiptText },
];

// ---------- Cashfree Keys ----------
function KeysSection() {
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("sandbox");
  const [appId, setAppId] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/cashfree");
    setCfg(data);
    setMode(data.active_mode);
    setAppId(data[data.active_mode]?.app_id || "");
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (cfg) setAppId(cfg[mode]?.app_id || "");
    setSecret("");
  }, [mode, cfg]);

  const save = async () => {
    if (!appId || !secret) return toast.error("Enter both App ID and Secret Key");
    setBusy(true);
    try {
      await api.post("/admin/cashfree", { mode, app_id: appId, secret_key: secret, enabled: true });
      toast.success(`${mode} keys saved & enabled`);
      setSecret("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setActive = async (m) => {
    try { await api.post("/admin/cashfree/mode", { active_mode: m }); toast.success(`Active mode: ${m}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const webhookUrl = `${BACKEND}${cfg?.webhook_path || "/api/payments/cashfree/webhook"}`;

  if (!cfg) return <div className="font-mono text-sm text-emerald-400">loading…</div>;

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 text-xs text-amber-300/90">
        Keys are stored encrypted and never exposed. Get them from Cashfree Dashboard → Developers → API Keys. The external Android app only receives the order_id & payment_session_id.
      </div>

      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Active mode</span>
        {["sandbox", "production"].map((m) => (
          <button
            key={m}
            data-testid={`active-mode-${m}`}
            onClick={() => setActive(m)}
            className={`rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-wider transition-all ${
              cfg.active_mode === m ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-[#1E2433] text-slate-400 hover:border-slate-600"
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {["sandbox", "production"].map((m) => (
          <div key={m} className={`rounded-xl border p-4 ${cfg[m]?.enabled ? "border-emerald-500/30" : "border-[#1E2433]"} bg-[#10131B]`}>
            <p className="flex items-center justify-between font-display text-sm font-bold uppercase text-slate-200">
              {m}
              {cfg[m]?.enabled ? <span className="flex items-center gap-1 text-[10px] font-normal text-emerald-400"><CheckCircle2 className="h-3 w-3" /> configured</span> : <span className="text-[10px] font-normal text-slate-500">not set</span>}
            </p>
            <p className="mt-1 truncate font-mono text-xs text-slate-500">{cfg[m]?.app_id || "—"}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4 rounded-xl border border-[#1E2433] bg-[#10131B] p-5">
        <Field label="Configure mode">
          <select data-testid="cf-mode-select" className={inputCls} value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="sandbox">Sandbox (Test)</option>
            <option value="production">Production (Live)</option>
          </select>
        </Field>
        <Field label="App ID (x-client-id)"><input data-testid="cf-appid" className={inputCls} value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="TEST xxxxxxxx / xxxxxxxx" /></Field>
        <Field label="Secret Key (x-client-secret)"><input data-testid="cf-secret" type="password" className={inputCls} value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Enter to set / update" /></Field>
        <button data-testid="cf-save" onClick={save} disabled={busy} className="co-btn-primary flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save {mode} keys
        </button>
      </div>

      <Field label="Webhook URL (add in Cashfree dashboard)">
        <div className="flex items-center gap-2">
          <input readOnly value={webhookUrl} className={`${inputCls} font-mono text-xs`} />
          <button data-testid="copy-webhook" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }} className="rounded-lg border border-[#1E2433] p-2.5 text-slate-400 hover:border-emerald-500/50 hover:text-emerald-400"><Copy className="h-4 w-4" /></button>
        </div>
      </Field>
    </div>
  );
}

// ---------- Plans ----------
function PlansSection() {
  const [plans, setPlans] = useState([]);
  const empty = { name: "", description: "", amount: "", duration_days: "30", active: true };
  const [f, setF] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => setPlans((await api.get("/admin/plans")).data), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: f.name, description: f.description, amount: parseFloat(f.amount), duration_days: parseInt(f.duration_days), active: f.active };
    try {
      if (editId) await api.put(`/admin/plans/${editId}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(editId ? "Plan updated" : "Plan created");
      setF(empty); setEditId(null); load();
    } catch (er) { toast.error(apiError(er)); } finally { setBusy(false); }
  };

  const edit = (p) => { setEditId(p.id); setF({ name: p.name, description: p.description, amount: String(p.amount), duration_days: String(p.duration_days), active: p.active }); };
  const del = async (id) => { await api.delete(`/admin/plans/${id}`); toast.success("Plan deleted"); load(); };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <form onSubmit={submit} data-testid="plan-form" className="space-y-4 rounded-xl border border-[#1E2433] bg-[#10131B] p-5">
        <p className="font-display text-lg font-bold text-slate-100">{editId ? "Edit plan" : "New plan"}</p>
        <Field label="Plan name"><input data-testid="plan-name" required className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Monthly" /></Field>
        <Field label="Description"><input data-testid="plan-desc" className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Amount (INR)"><input data-testid="plan-amount" required type="number" step="0.01" min="1" className={inputCls} value={f.amount} onChange={(e) => set("amount", e.target.value)} /></Field>
          <Field label="Duration (days)"><input data-testid="plan-duration" required type="number" min="1" className={inputCls} value={f.duration_days} onChange={(e) => set("duration_days", e.target.value)} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-emerald-400" /> Active
        </label>
        <div className="flex gap-2">
          <button data-testid="plan-submit" disabled={busy} className="co-btn-primary flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Update" : "Create"}
          </button>
          {editId && <button type="button" onClick={() => { setEditId(null); setF(empty); }} className="rounded-lg border border-[#1E2433] px-4 text-sm text-slate-400 hover:border-slate-600">Cancel</button>}
        </div>
      </form>

      <div className="space-y-3" data-testid="plans-list">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-row-${p.id}`} className="flex items-center gap-3 rounded-xl border border-[#1E2433] bg-[#10131B] p-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-emerald-500/20 bg-emerald-500/10 text-emerald-400"><Layers className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-base font-bold text-slate-100">{p.name} {!p.active && <span className="ml-1 text-[10px] text-slate-500">(inactive)</span>}</p>
              <p className="font-mono text-xs text-slate-500">₹{p.amount} · {p.duration_days} days</p>
            </div>
            <button onClick={() => edit(p)} data-testid={`plan-edit-${p.id}`} className="rounded-lg border border-[#1E2433] px-3 py-1.5 text-xs text-slate-300 hover:border-emerald-500/50">Edit</button>
            <button onClick={() => del(p.id)} data-testid={`plan-delete-${p.id}`} className="rounded-lg border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Coupons ----------
function CouponsSection() {
  const [coupons, setCoupons] = useState([]);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percentage");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => setCoupons((await api.get("/admin/coupons")).data), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/coupons", { code, discount_type: type, discount_value: parseFloat(value) });
      toast.success("Coupon created");
      setCode(""); setValue(""); load();
    } catch (er) { toast.error(apiError(er)); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); toast.success("Coupon deleted"); load(); };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <form onSubmit={submit} data-testid="coupon-form" className="space-y-4 rounded-xl border border-[#1E2433] bg-[#10131B] p-5">
        <p className="font-display text-lg font-bold text-slate-100">New coupon</p>
        <Field label="Coupon code"><input data-testid="coupon-code" required className={`${inputCls} uppercase`} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SAVE20" /></Field>
        <Field label="Discount type">
          <div className="flex gap-2">
            {[{ v: "percentage", icon: Percent, l: "Percentage" }, { v: "flat", icon: IndianRupee, l: "Flat amount" }].map((o) => (
              <button key={o.v} type="button" data-testid={`coupon-type-${o.v}`} onClick={() => setType(o.v)} className={`flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm transition-all ${type === o.v ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-[#1E2433] text-slate-400 hover:border-slate-600"}`}>
                <o.icon className="h-4 w-4" /> {o.l}
              </button>
            ))}
          </div>
        </Field>
        <Field label={type === "percentage" ? "Discount (%)" : "Discount (₹)"}><input data-testid="coupon-value" required type="number" step="0.01" min="0" className={inputCls} value={value} onChange={(e) => setValue(e.target.value)} /></Field>
        <button data-testid="coupon-submit" disabled={busy} className="co-btn-primary flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create coupon
        </button>
      </form>

      <div className="space-y-3" data-testid="coupons-list">
        {coupons.length === 0 && <p className="py-8 text-center text-sm text-slate-500">No coupons yet.</p>}
        {coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-row-${c.code}`} className="flex items-center gap-3 rounded-xl border border-dashed border-emerald-500/30 bg-[#10131B] p-4">
            <Ticket className="h-5 w-5 text-emerald-400" />
            <div className="flex-1">
              <p className="font-mono text-base font-bold tracking-wider text-emerald-300">{c.code}</p>
              <p className="text-xs text-slate-500">{c.discount_type === "percentage" ? `${c.discount_value}% off` : `₹${c.discount_value} off`}</p>
            </div>
            <button onClick={() => del(c.id)} data-testid={`coupon-delete-${c.code}`} className="rounded-lg border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Orders ----------
function OrdersSection() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setOrders(data)); }, []);
  const badge = (s) => s === "PAID" ? "text-emerald-400 border-emerald-500/30" : s === "ACTIVE" ? "text-amber-400 border-amber-500/30" : "text-slate-400 border-slate-700";
  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.length === 0 && <p className="py-10 text-center text-sm text-slate-500">No orders yet. Orders appear here once payments are created.</p>}
      {orders.map((o) => (
        <div key={o.order_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#1E2433] bg-[#10131B] p-4">
          <div className="min-w-0 flex-1">
            <p className="font-mono text-xs text-slate-500">{o.order_id}</p>
            <p className="font-display text-base font-bold text-slate-100">{o.plan_name} · ₹{o.amount} {o.coupon_code && <span className="ml-1 font-mono text-xs text-emerald-400">({o.coupon_code} −₹{o.discount})</span>}</p>
          </div>
          <span className={`rounded border px-2 py-0.5 font-mono text-[10px] uppercase ${badge(o.status)}`}>{o.status}</span>
          <span className="rounded border border-slate-700 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-400">{o.mode}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- Subscribe / Test checkout modal ----------
function SubscribeModal({ onClose }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [coupon, setCoupon] = useState("");
  const [preview, setPreview] = useState(null);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/subscription/plans").then(({ data }) => { setPlans(data); if (data[0]) setPlanId(data[0].id); }); }, []);

  const checkCoupon = async () => {
    if (!coupon) return setPreview(null);
    try { const { data } = await api.post("/subscription/validate-coupon", { code: coupon, plan_id: planId }); setPreview(data); toast.success("Coupon applied"); }
    catch (e) { setPreview(null); toast.error(apiError(e)); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/orders", {
        plan_id: planId,
        coupon_code: coupon || null,
        customer_id: "admin-test",
        customer_name: "Admin Test",
        customer_email: "admin@crazyone.app",
        customer_phone: "9999999999",
        return_url: `${window.location.origin}/payment-result`,
      });
      setOrder(data);
      if (window.Cashfree && data.payment_session_id) {
        const cashfree = window.Cashfree({ mode: data.mode === "production" ? "production" : "sandbox" });
        const result = await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_modal" });
        if (result?.error) toast.error(result.error.message || "Payment cancelled");
        const { data: v } = await api.post("/payments/verify", { order_id: data.order_id });
        setOrder((o) => ({ ...o, verified: v }));
        toast[v.order_status === "PAID" ? "success" : "message"](`Order status: ${v.order_status}`);
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const amount = preview?.final_amount ?? plans.find((p) => p.id === planId)?.amount;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} data-testid="subscribe-modal" className="w-full max-w-md space-y-4 rounded-2xl border border-[#1E2433] bg-[#10131B] p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-slate-100">Subscribe (test checkout)</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="h-5 w-5" /></button>
        </div>
        <Field label="Plan">
          <select data-testid="sub-plan" className={inputCls} value={planId} onChange={(e) => { setPlanId(e.target.value); setPreview(null); }}>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — ₹{p.amount}</option>)}
          </select>
        </Field>
        <Field label="Coupon (optional)">
          <div className="flex gap-2">
            <input data-testid="sub-coupon" className={`${inputCls} uppercase`} value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="SAVE20" />
            <button type="button" data-testid="sub-apply-coupon" onClick={checkCoupon} className="rounded-lg border border-emerald-500/40 px-4 text-sm text-emerald-300 hover:bg-emerald-500/10">Apply</button>
          </div>
        </Field>
        <div className="rounded-xl border border-[#1E2433] bg-[#0b0d13] p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">Payable</span>
            <span className="font-display text-2xl font-extrabold text-slate-100">₹{amount ?? "—"}</span>
          </div>
          {preview && <p className="mt-1 text-right text-xs text-emerald-400">You save ₹{preview.discount}</p>}
        </div>
        <button data-testid="sub-pay" onClick={pay} disabled={busy || !planId} className="co-btn-primary flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />} Pay with Cashfree
        </button>
        {order && (
          <pre data-testid="sub-order-json" className="max-h-48 overflow-auto rounded-lg border border-[#1E2433] bg-[#0b0d13] p-3 font-mono text-[10px] text-slate-400">{JSON.stringify(order, null, 2)}</pre>
        )}
        <p className="text-center text-[10px] text-slate-600">The Android app calls <span className="font-mono text-slate-500">/api/payments/orders</span> and uses the returned order_id + payment_session_id.</p>
      </motion.div>
    </div>
  );
}

export default function PaymentsPanel() {
  const [sub, setSub] = useState("keys");
  const [showSubscribe, setShowSubscribe] = useState(false);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 overflow-x-auto">
          {SUBTABS.map((t) => (
            <button key={t.id} data-testid={`ptab-${t.id}`} onClick={() => setSub(t.id)} className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${sub === t.id ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-[#1E2433] text-slate-400 hover:border-slate-600"}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>
        <button data-testid="open-subscribe" onClick={() => setShowSubscribe(true)} className="co-btn-primary flex items-center gap-2 rounded-lg px-4 py-2 text-sm">
          <CreditCard className="h-4 w-4" /> Subscribe
        </button>
      </div>

      {sub === "keys" && <KeysSection />}
      {sub === "plans" && <PlansSection />}
      {sub === "coupons" && <CouponsSection />}
      {sub === "orders" && <OrdersSection />}

      {showSubscribe && <SubscribeModal onClose={() => setShowSubscribe(false)} />}
    </div>
  );
}
